import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import classNames from "classnames";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { toast } from "@jti/core-components/components/Toaster";
import { LoyaltyApi } from "@jti/rest-api";
import { type GetPrizesElementResponseType, type GetPrizesInputType } from "@jti/rest-api-types";
import { useSelector } from "@jti/store/reduxHooks";
import { selectIsUserDataFetching } from "@jti/store/selectors/selectIsUserDataFetching";
import { type RootStoreState } from "@jti/store/types";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { LoadMore } from "@jti/ui/components/LoadMore";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import useLoggedUser from "../../hooks/useLoggedUser";
import type { RedeemDialogLabels } from "../RedeemDialog/RedeemDialog";
import { RedeemDialog } from "../RedeemDialog/RedeemDialog";

import ItemsList from "./ItemsList/ItemsList";

import styles from "./SimplifiedPrizes.module.scss";

export type SimplifiedPrizesTeaserDataType = {
  teaserTitle: string;
  teaserLinkUrl: string;
  teaserLinkText: string;
};

export type SimplifiedPrizesProps = {
  discoverUrlParent?: string;
  discoverButtonText?: string;
  ctaLabel: string;
  color?: "white" | "black";
  noOffersText: string;
  confirmationMessage?: string;
  confirmationMessagePlural?: string;
  newOfferDays?: number;
  newLabel: string;
  oneDayLabel?: string;
  daysLabel?: string;
  isTeaser: boolean;
  pagination: number;
  teaserData: SimplifiedPrizesTeaserDataType;
  loadMoreButtonLabel: string;
  errorMessage?: string;
  outOfStockLabel: string;
  earnPointsCtaText?: string;
  earnPointsCtaUrl?: string;
} & RedeemDialogLabels;

const teaserFallbackData = { teaserTitle: "", teaserLinkUrl: "", teaserLinkText: "" };
/**
 * Legacy Component for displaying SimplifiedPrizes with a prize locator and RedeemDialog for AEM only.
 * This component is deprecated and should be replaced with SimplifiedPrizesV2
 * kept for backward compatibility with AEM and migration script.
 **/
export const SimplifiedPrizes: FC<SimplifiedPrizesProps> = ({
  ctaLabel = "Redeem now",
  pointsLabel = "points",
  costLabel = "Cost",
  availablePointsLabel = "Available Points",
  balanceAfterRedemptionLabel = "Balance after redemption",
  primaryCta = "Confirm",
  secondaryCta = "Cancel",
  dialogTitle = "Confirm your reward",
  dialogTitleConfirmation = "Congratulations",
  confirmationMessage = "{#redeemedPoints#} points redeemed for {#voucherName#}",
  confirmationMessagePlural = "{#redeemedPoints#} points redeemed for {#qty#} x {#voucherName#}",
  codeLabel = "Your code:",
  codeInfo = "Your code is stored in <a href='#'> points transaction history</a>.",
  bottomText = "By clicking you agree to our <a href='#'> terms and conditions</a>.",
  color = "white",
  noOffersText,
  emailHint,
  emailLabel,
  newOfferDays,
  newLabel = "New",
  oneDayLabel,
  daysLabel,
  unitLabel,
  isTeaser,
  teaserData = teaserFallbackData,
  pagination = 3,
  loadMoreButtonLabel = "Load more",
  errorMessage = "Something went wrong. Please try again later.",
  outOfStockLabel = "Out of stock",
  externalCodeButtonLabel = "Get your code",
  earnPointsCtaText,
  earnPointsCtaUrl,
  discoverUrlParent,
  discoverButtonText,
  quantityAriaLabel,
  increaseAriaLabel,
  decreaseAriaLabel,
}) => {
  const [dialogData, setDialogData] = useState<GetPrizesElementResponseType | Record<string, never>>({});
  const [prizes, setPrizes] = useState<GetPrizesElementResponseType[]>([]);
  const [page, setPage] = useState(1);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState(!isTeaser);
  const [hasError, setHasError] = useState(false);
  const [isPrizesLoaded, setIsPrizesLoaded] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tier = useSelector((state: RootStoreState) => state.loyaltyCore.tier);
  const { __ } = useTranslations();
  const {
    points: { balance },
  } = useSelector((state: RootStoreState) => state.loyaltyCore);
  const isUserDataFetching = useSelector(selectIsUserDataFetching);
  const [isLogged] = useLoggedUser();
  const { teaserTitle, teaserLinkUrl, teaserLinkText } = teaserData;

  const className = classNames(styles.simplifiedPrizes, {
    [styles.teaser]: isTeaser,
  });

  const resetDialogData = () => setDialogData({});

  const dialogLabels = {
    costLabel,
    availablePointsLabel,
    balanceAfterRedemptionLabel,
    primaryCta,
    secondaryCta,
    dialogTitle,
    dialogTitleConfirmation,
    bottomText,
    pointsLabel,
    confirmationMessage,
    confirmationMessagePlural,
    codeLabel,
    codeInfo,
    emailHint,
    emailLabel,
    unitLabel,
    externalCodeButtonLabel,
    quantityAriaLabel,
    increaseAriaLabel,
    decreaseAriaLabel,
  };

  const handleGetPrizesRequest = useCallback(
    ({ sort, orderBy }: Pick<GetPrizesInputType, "sort" | "orderBy">) => {
      return LoyaltyApi.init().getPrizes({
        page: page,
        perPage: pagination,
        sort: sort,
        orderBy: orderBy,
        is_virtual: "1",
      });
    },
    [page, pagination]
  );

  const handleLoadMoreClick = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const response = await handleGetPrizesRequest({ sort: "asc", orderBy: "points" });

      const updatedPrizes = [...prizes, ...response.items];

      if (updatedPrizes.length >= response.total) {
        setShowLoadMoreButton(false);
      }

      setPrizes(updatedPrizes);
      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      console.error(error);
      toast.error(__("mobile.error.something_went_wrong"));
    } finally {
      setIsLoadingMore(false);
    }
  }, [handleGetPrizesRequest, prizes, __]);

  const loadMoreButton = useMemo(() => {
    if (isTeaser || !showLoadMoreButton) return null;

    return isLoadingMore ? (
      <DEPRECATED_Loading dataTestId="loadMoreLoading" />
    ) : (
      <LoadMore label={loadMoreButtonLabel} data-testid="simplifiedPrizesLoadMore" onClick={handleLoadMoreClick} />
    );
  }, [isTeaser, showLoadMoreButton, isLoadingMore, loadMoreButtonLabel, handleLoadMoreClick]);

  useEffect(() => {
    const fetchPrizes = async () => {
      const sortType = isTeaser ? "desc" : "asc";
      const orderByType = isTeaser ? "created_at" : "points";

      try {
        const response = await handleGetPrizesRequest({ sort: sortType, orderBy: orderByType });
        if (response.items.length >= response.total || response.total <= pagination) {
          setShowLoadMoreButton(false);
        }

        setPrizes(response.items);
        setPage((prevPage) => prevPage + 1);
      } catch (error) {
        console.error(error);
        setHasError(true);
      } finally {
        setIsPrizesLoaded(true);
      }
    };
    fetchPrizes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hasError)
    return (
      <Typography variant="h2" weight="light" className={styles.error}>
        {errorMessage}
      </Typography>
    );

  return (
    <div className={className}>
      {isTeaser && (
        <Typography variant="body2" weight="light" className={styles.title}>
          {teaserTitle}
        </Typography>
      )}
      {isPrizesLoaded && !isUserDataFetching ? (
        <div className={styles.listWrapper}>
          <ItemsList
            discoverButtonText={discoverButtonText}
            discoverUrlParent={discoverUrlParent}
            items={prizes}
            pointsLabel={pointsLabel}
            ctaLabel={ctaLabel}
            color={color}
            noOffersText={noOffersText}
            points={balance}
            setDialogData={setDialogData}
            newBadgeData={{ newOfferDays: newOfferDays, label: newLabel }}
            limitedOfferLabels={{ daysLabel, oneDayLabel }}
            tier={tier.name}
            outOfStockLabel={outOfStockLabel}
            isLogged={isLogged}
            earnPointsCtaText={earnPointsCtaText}
            earnPointsCtaUrl={earnPointsCtaUrl}
            isLegacy={true}
          />
          <div className={styles.button}>{loadMoreButton}</div>
          {isTeaser && (
            <Button
              variant="text-muted"
              size="sm"
              className={styles.link}
              href={teaserLinkUrl}
              iconAfter={LongArrowForwardIcon}
            >
              {teaserLinkText}
            </Button>
          )}
        </div>
      ) : (
        <DEPRECATED_Loading dataTestId={"prizesLoading"} className={styles.loader} />
      )}

      <RedeemDialog
        pointsBalance={balance}
        prizeData={dialogData}
        onCloseDialog={resetDialogData}
        labels={dialogLabels}
        isOpened={!!dialogData.prize_uuid}
      />
    </div>
  );
};
